import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { header, handleError } from 'src/app/_helpers/tools/header.tool';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';

import { parseToObject } from 'src/app/_helpers/tools/permission.tool';
import { UserModel } from '../_models/user.model';
import { StorageService } from './storage.service';
// import { Response } from '../_models/auth.model';
// import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

// TRV-2790: /detail devuelve la fila completa de users. Para la sesión se quitan los secretos
// (clave, códigos, fcm) y lo que /detail agrega y el login no trae, para que auth.user quede como en el login
const SESSION_USER_OMITTED_FIELDS = [
  'password',
  'verify_code',
  'validation_codes',
  'fcm_token',
  'roles',
  'leader',
  'pastor',
  'church',
  'network',
  'siesaCategories',
  'leader_generation_three',
];

const sameEmail = (a: any, b: any) =>
  typeof a === 'string' && typeof b === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();

// null si el perfil no es la cuenta recién registrada o no trae un id numérico
const toSessionUser = (profile: any, signupUser: any): any => {
  if (!profile || !Number.isInteger(profile.id) || profile.id <= 0 || !sameEmail(profile.email, signupUser?.email)) {
    return null;
  }
  const user = { ...profile };
  SESSION_USER_OMITTED_FIELDS.forEach((field) => delete user[field]);
  user.sub_account = false;
  return user;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private isLoadingSubject: BehaviorSubject<boolean>;
  // TRV-2790: la sesión de signup-mci espera en memoria hasta que el código se valida;
  // nunca pasa por localStorage antes de eso
  private signupSession: any = null;
  // private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  constructor(
    private http: HttpClient,
    private _storageService: StorageService,
    // private authHttpService: AuthHTTPService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(email: string, password: string, login_module?:any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth`,
        { email, password, platform: 'conexion12', login_module  },
        { headers: header }
      )
      .pipe(
        map((auth: any) => {
          // auth.entity[0].objectsList = await parseToObject(JSON.parse(auth.entity[0].listObjetos), "Code", "Obj");
          if (auth.user.verify) {
            this.setAuthOnLocalStorage(auth);
            this.isLoadingSubject.next(false);
          }
          return auth;
        }),
        catchError(handleError)
      );
  }

  // Con correo o con Google, la sesión de signup-mci espera en memoria: su usuario no trae id
  // y el teléfono viene enmascarado. sendTokenUserRegister la arma con /detail
  registerUser(body) {
    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth/signup-mci`,
        body,
        { headers: header }
      )
      .pipe(
        map((auth: any) => {
          this.signupSession = auth;
          this.isLoadingSubject.next(false);
          return auth;
        }),
        catchError(handleError)
      );
  }

  logout() {
    this._storageService.removeItem('auth');
    this._storageService.removeItem('permissions');
    this._storageService.removeItem('initialUser');
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  RemoveUserStorage(): void {
    // Limpia el BehaviorSubject
    this.currentUserSubject.next(null);
    this._storageService.removeItem('auth');
    this._storageService.removeItem('permissions');
    this._storageService.removeItem('initialUser');
  }

  refreshToken() {
    let token = this._storageService.getItem('auth').token;
    let session_id = this._storageService.getItem('auth').session_id;

    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth/refresh-token`,
        { token, session_id },
        // { headers: _header }
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError(handleError)
      );
  }

  getUserByToken(): Observable<any> {
    const { user } = this.getAuthFromLocalStorage() || { user: null };
    if (!user) {
      return of(undefined);
    }
    this.currentUserSubject = new BehaviorSubject<any>(user);
    return new Observable((e) => {
      e.next(user);
    });
  }

  forgotPassword(data: { email: string; url: string }): Observable<Response> {
    this.isLoadingSubject.next(true);
    return this.http
      .post<Response>(
        `${environment.apiUrlG12Connect.users}/auth/send-recovery`,
        JSON.stringify(data),
        { headers: header }
      )
      .pipe(
        map((auth: Response) => {
          this.isLoadingSubject.next(false);
          return auth;
        }),
        catchError(handleError),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  // private methods
  private setAuthOnLocalStorage(auth: Response): boolean {
    // store auth accessToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    // Es síncrono a propósito: como async sin await, cualquier fallo de escritura
    // se perdía en una promesa rechazada y el usuario rebotaba al login sin explicación.
    if (!auth) { return false; }
    this._storageService.setItem('initialUser', auth);
    this._storageService.setItem('auth', auth);
    return true;
  }

  private getAuthFromLocalStorage(): UserModel {
    try {
      return this._storageService.getItem('auth');
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  changePassword(payload) {
    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth/reset-password`,
        payload,
        { headers: header }
      )
      .pipe(
        map((auth: any) => {
          this.isLoadingSubject.next(false);
          return auth;
        }),
        catchError(handleError)
      );
  }

  sendCode({ userCode, type }: { userCode: string; type: 'email' | 'phone' }) {
    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth/send-code-mci-account`,
        { userCode, type },
        { headers: header }
      )
      .pipe(
        map((auth: any) => {
          this.isLoadingSubject.next(false);
          return auth;
        }),
        catchError(handleError)
      );
  }

  validateCode({ userCode, code, name, last_name, mci_platform }: {
    type: 'code';
    userCode: string;
    code: string;
    name: string;
    last_name: string;
    mci_platform: boolean;
  }) {
    return this.http
      .post<any>(
        `${environment.apiUrlG12Connect.users}/auth/validate-code-or-create-password`,
        { type: 'code', userCode, code, name, last_name, mci_platform },
        { headers: header }
      )
      .pipe(
        map((auth: any) => {
          this.isLoadingSubject.next(false);
          return auth;
        }),
        catchError(handleError)
      );
  }

  // Arma la sesión una sola vez con el token de signup-mci y el usuario de /detail, como la del login.
  // false si ya no está en memoria (la página se recargó) o si /detail falla o no es la misma cuenta
  sendTokenUserRegister(): Observable<boolean> {
    const session = this.signupSession;
    this.signupSession = null;
    if (!session?.token) {
      return of(false);
    }
    return this.http
      .get<any>(`${environment.apiUrlG12Connect.users}/detail`, {
        headers: new HttpHeaders({ Authorization: `Bearer ${session.token}` }),
      })
      .pipe(
        map((profile: any) => {
          const user = toSessionUser(profile, session.user);
          return user ? this.setAuthOnLocalStorage({ token: session.token, user } as any) : false;
        }),
        catchError(() => of(false))
      );
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
